import proj4 from "proj4";
import { createSelector } from "reselect";
import { observeState } from "@mapsight/lib-redux/observe-state";
import { mergeAll, set } from "@mapsight/core/lib/base/actions";
import { createFilteredFeatureSourceSelector } from "@mapsight/core/lib/feature-sources/selectors";
import { activateInteraction, deactivateInteraction, } from "@mapsight/core/lib/map/actions";
import { DEFAULT_DISPLAY_STYLE, DEFAULT_DRAW_STYLE, DEFAULT_PROJECTION, } from "@mapsight/core/mixins/EditorMixin";
import { di } from "@mapsight/core/ol-proxy";
import DrawInteraction from "@mapsight/core/ol-proxy/definitions/interaction/DrawInteraction";
import { mapViewCenter } from "../../config";
import { FEATURE_SELECTIONS, FEATURE_SOURCES, MAP, } from "../../config/constants/controllers";
import { features, metaData } from "../../config/map/layers";
import { translate } from "../../helpers/i18n";
export const createActivateAction = (mapController, name) => activateInteraction(mapController, `${name}_drawInteraction`);
export const createDeactivateAction = (mapController, name) => deactivateInteraction(mapController, `${name}_drawInteraction`);
function geoJsonPointToMapViewCoordinates(pointGeometry) {
    return proj4(DEFAULT_PROJECTION, "EPSG:3857", pointGeometry.coordinates);
}
/**
 * @param {object} options options
 * @param {import('redux').Store} options.store store
 * @param {string} options.name name name of the editor (must be unique per mapsight instance)
 * @param {string} options.mapControllerName map controller name
 * @param {string} options.featureSourcesControllerName feature sources controller name
 * @param {string} options.featureSelectionsControllerName feature selections controller name
 * @param {string} options.displayStyle style for finished draw
 * @param {string} options.drawStyle style while drawing
 */
function setupDrawInteraction({ store, name, featureSourcesControllerName, featureSelectionsControllerName, mapControllerName, drawStyle, displayStyle, }) {
    const fSId = `${name}_featureSource`;
    const drawLayerId = `${name}_drawLayer`;
    const interactionId = `${name}_drawInteraction`;
    const vFS = {
        type: "VectorFeatureSource",
        options: {
            projection: DEFAULT_PROJECTION,
            featureSourceId: fSId,
            featureSourcesControllerName: featureSourcesControllerName,
            featureSelectionsControllerName: featureSelectionsControllerName,
            canAnimate: false,
            canCluster: false,
            featureSelections: [],
        },
    };
    store.dispatch(mergeAll({
        [mapControllerName]: {
            interactions: {
                [interactionId]: {
                    type: "DrawInteraction",
                    options: {
                        active: false,
                        type: "Point",
                        style: drawStyle,
                        replacePrevious: true,
                        clearOnStart: true,
                        source: vFS,
                        stopClick: true,
                    },
                },
            },
            layers: {
                [drawLayerId]: {
                    type: "VectorLayer",
                    options: {
                        visible: true,
                        style: displayStyle,
                        renderBuffer: 200,
                        selections: [],
                        source: vFS,
                    },
                },
            },
        },
        [featureSourcesControllerName]: {
            [fSId]: {
                enableHistory: false,
                data: {},
                isLoading: true,
            },
        },
    }));
    const featuresSelector = createSelector(createFilteredFeatureSourceSelector(featureSourcesControllerName, fSId, mapControllerName), (featureSourceState) => { var _a; return (_a = featureSourceState === null || featureSourceState === void 0 ? void 0 : featureSourceState.data) === null || _a === void 0 ? void 0 : _a.features; });
    const deactivate = createDeactivateAction(mapControllerName, name);
    observeState(store, featuresSelector, (createdFeatures) => {
        var _a;
        if (createdFeatures === null || createdFeatures === void 0 ? void 0 : createdFeatures.length) {
            const feature = createdFeatures[0];
            if (((_a = feature === null || feature === void 0 ? void 0 : feature.geometry) === null || _a === void 0 ? void 0 : _a.type) === "Point") {
                store.dispatch(deactivate);
            }
        }
    });
}
/**
 * @type {MarkerFeatureFactory}
 */
function defaultCreateMarkerFeature(point, { id = "link-marker", name = "Marker", iconId = "marker" }) {
    return /** @type {import('../../types').MapsightUiFeature} */ {
        type: "Feature",
        geometry: point,
        id: id,
        properties: {
            id: id,
            name: name,
            mapsightIconId: iconId,
        },
    };
}
/**
 * @param {object} options options
 * @param {import('redux').Store} options.store store
 * @param {string} options.name name name of the editor (must be unique per mapsight instance)
 * @param {string} options.mapControllerName map controller name
 * @param {string} options.featureSourcesControllerName feature sources controller name
 * @param {string} options.linkParameter link parameter name
 * @param {boolean} options.centerOnMarker centers map on marker if present
 * @param {string} options.markerStyle style for marker
 * @param {number|null} options.markerZoom zoom level when marker is present
 * @param {import('../../types').MapsightUiFeatureId} options.markerFeatureId feature id
 * @param {string} options.markerName marker feature name
 * @param {string|null} options.markerLayerGroup marker layer group
 * @param {MarkerFeatureFactory} options.createMarkerFeature factory for marker feature
 */
function setupMarker(options) {
    var _a;
    const { store, name, linkParameter, mapControllerName, featureSourcesControllerName, markerStyle, centerOnMarker, markerZoom, markerFeatureId, markerName, markerLayerGroup, markerIconId, createMarkerFeature, } = options;
    const fSId = `${name}_markerFeatureSource`;
    const layerId = `${name}_markerLayer`;
    const regex = new RegExp(`[#&]${encodeURIComponent(linkParameter)}=(\\d+(?:\\.\\d+)?)\\/(\\d+(?:\\.\\d+)?)(?:&|$)`, "");
    // TODO: Handle on SSR?!
    // TODO: Handle on-page-navigation (on hashchange/pushState)
    const linkMarkerMatches = typeof window !== "undefined" && ((_a = window.location) === null || _a === void 0 ? void 0 : _a.hash.match(regex));
    if (!linkMarkerMatches) {
        return;
    }
    const [, lat, lon] = linkMarkerMatches;
    const point = {
        type: "Point",
        coordinates: [parseFloat(lon), parseFloat(lat)],
    };
    const feature = createMarkerFeature(point, {
        id: markerFeatureId,
        name: markerName,
        iconId: markerIconId,
    });
    store.dispatch(mergeAll({
        [mapControllerName]: {
            layers: {
                [layerId]: features(fSId, true, true, metaData(markerName, null, false, false, false, markerLayerGroup), markerStyle),
            },
        },
        [featureSourcesControllerName]: {
            [fSId]: {
                enableHistory: false,
                data: {
                    type: "FeatureCollection",
                    features: [feature],
                },
                ids: [feature.id],
            },
        },
    }));
    if (centerOnMarker) {
        store.dispatch(set([mapControllerName, "view", "center"], mapViewCenter(...geoJsonPointToMapViewCoordinates(point))));
        if (Number.isInteger(markerZoom)) {
            store.dispatch(set([mapControllerName, "view", "zoom"], markerZoom));
        }
    }
}
/**
 * This plugin will allow drawing a link maker (to be used with the appropriate component)
 * and/or display such a marker.
 *
 * @param options options
 * @returns plugin
 */
export default function createShareLinkPlugin(options = {}) {
    const { name = "sharePositionLink", enableDrawing = true, mapControllerName = MAP, featureSourcesControllerName = FEATURE_SOURCES, featureSelectionsControllerName = FEATURE_SELECTIONS, displayStyle = DEFAULT_DISPLAY_STYLE, drawStyle = DEFAULT_DRAW_STYLE, drawInteraction = DrawInteraction, linkParameter = "lm", showMarker = true, centerOnMarker = true, markerStyle = DEFAULT_DISPLAY_STYLE, markerZoom = 16, markerFeatureId = "link-marker", markerName = translate("marker"), markerLayerGroup = null, createMarkerFeature = defaultCreateMarkerFeature, } = options;
    return {
        afterCreate({ store }) {
            if (enableDrawing) {
                if (drawInteraction) {
                    di.injectDefinitions([drawInteraction]);
                }
                setupDrawInteraction({
                    name,
                    store,
                    mapControllerName,
                    featureSourcesControllerName,
                    featureSelectionsControllerName,
                    drawStyle,
                    displayStyle,
                });
            }
            if (showMarker) {
                setupMarker({
                    name,
                    store,
                    mapControllerName,
                    featureSourcesControllerName,
                    linkParameter,
                    centerOnMarker,
                    markerStyle,
                    markerZoom,
                    markerFeatureId,
                    markerName,
                    markerLayerGroup,
                    createMarkerFeature,
                });
            }
        },
    };
}
